import { type CSSProperties, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { MdCategory } from "react-icons/md";
import { toGetServiceCategoriesTree } from "../../apis/actions/vehicle.actions";
import { AppDispatch, RootState } from "../../apis/store";
import { normalizeServiceTree } from "../../utils/serviceCategoryTree";
import ShopCategoryFilter, {
  type ShopFilterCategory,
} from "./ShopCategoryFilter";

type Props = {
  selectedId: string | null;
  onSelect: (categoryId: string | null) => void;
};

const categoryIconBase = `${process.env.PUBLIC_URL ?? ""}/assets/imgs/service-categories`;

export const getServiceCategoryIconSrc = (name: string): string => {
  const n = name.toLowerCase().replace(/[\s_-]+/g, "");
  if (n.includes("autochapeau")) return `${categoryIconBase}/autochapeau.svg`;
  if (n.includes("autoflex")) return `${categoryIconBase}/autoflex.svg`;
  if (n.includes("maintenance")) return `${categoryIconBase}/maintenance.svg`;
  if (n.includes("warranty")) return `${categoryIconBase}/warranty.svg`;
  return `${categoryIconBase}/default.svg`;
};

const iconForCategory = (category: ShopFilterCategory | null) => {
  if (!category) return <MdCategory />;

  // Masked rather than an <img>: the artwork hardcodes its own stroke colour,
  // and the chip needs it to follow the theme (and turn white when selected).
  return (
    <span
      className="ac-shop-category__glyph"
      style={
        {
          "--ac-category-glyph": `url("${getServiceCategoryIconSrc(category.name)}")`,
        } as CSSProperties
      }
    />
  );
};

const ServiceCategoryFilter = ({ selectedId, onSelect }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { serviceCategoryTree } = useSelector(
    (state: RootState) => state.vehicle
  );

  useEffect(() => {
    dispatch(toGetServiceCategoriesTree({ lang: i18n.language }));
  }, [dispatch, i18n.language]);

  const categories = useMemo(
    () => normalizeServiceTree(serviceCategoryTree?.list),
    [serviceCategoryTree?.list]
  );

  return (
    <ShopCategoryFilter
      categories={categories as ShopFilterCategory[]}
      isLoading={
        serviceCategoryTree?.status === "loading" ||
        serviceCategoryTree?.status === "idle"
      }
      hasFailed={serviceCategoryTree?.status === "rejected"}
      selectedId={selectedId}
      onSelect={onSelect}
      eyebrow={t("services.serviceFilter.eyebrow")}
      title={t("services.serviceFilter.title")}
      allLabel={t("services.serviceFilter.all")}
      renderIcon={iconForCategory}
    />
  );
};

export default ServiceCategoryFilter;
